<script setup lang="ts">
import { capitalize } from 'lodash'
import { computed, onMounted, ref, watch } from 'vue'
import { useExtendedI18n } from '@/i18n'
import { RouterLinkBack, VButtonInvisible, VSection, VTable } from '@/modules/shared/components'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { rails_url } from '@/modules/shared/utils/rails'
import { useEntityStore } from '../stores/entity-store'
import ChecklistEntitiesNav from '../component/checklist-entities-nav.vue'
import ManageEntities from '../component/manage-entities.vue'
import AllGroup from '../component/all-group.vue'
import InProgress from '../component/in-progress.vue'
import { useRoute } from 'vue-router'
///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { t } = useExtendedI18n()

const entityStore = useEntityStore()

const allGroup = computed(() => entityStore.allGroup)
const inProgress = computed(() => entityStore.inProgressEntities)
const manageEntities = computed(() => entityStore.manageEntities)

const tabs = [
  {
    label: t('All Groups', 0),
    to: { name: 'admin.all-group' },
  },
  {
    label: t('Managed Funds', 0),
    to: { name: 'admin.managed-funds' },
  },
  {
    label: t('Managed SPVs', 0),
    to: { name: 'admin.managed-spvs' },
  },
  {
    label: t('Funds in Progress', 0),
    to: { name: 'admin.funds-in-progress' },
  },
  {
    label: t('SPVs in Progress', 0),
    to: { name: 'admin.spv-in-progress' },
  },
  {
    label: t('SPVs On Hold', 0),
    to: { name: 'admin.spvs-on-hold' },
  },
  {
    label: t('Fund Formation in Progress', 0),
    to: { name: 'admin.fund-formation-in-progress' },
  },
]

const route = useRoute()
const routes = [
  'all-group',
  'managed-funds',
  'managed-spvs',
  'funds-in-progress',
  'spv-in-progress',
  'spvs-on-hold',
  'fund-formation-in-progress',
]

const currentTabIndex = computed(() => {
  const segments = route.path.split('/').filter(Boolean)
  const last = segments[segments.length - 1]
  const index = routes.indexOf(last)
  return index !== -1 ? index : 0
})

const skeleton = ref(true)

const emptyMessageForTab = computed(() => {
  switch (currentTabIndex.value) {
    case 3:
      return t('No funds in progress.', 0)
    case 4:
      return t('No SPVs in progress.', 0)
    case 5:
      return t('No SPVs on hold.', 0)
    case 6:
      return t('No funds in formation.', 0)
    default:
      return undefined
  }
})

const fetchTabData = async () => {
  skeleton.value = true
  try {
    switch (currentTabIndex.value) {
      case 0:
        await entityStore.fetchAllGroup()
        break
      case 1:
        await entityStore.fetchManageEntities('fund')
        break
      case 2:
        await entityStore.fetchManageEntities('spv')
        break
      case 3:
        await entityStore.fetchFundsInProgress()
        break
      case 4:
        await entityStore.fetchSpvsInProgress()
        break
      case 5:
        await entityStore.fetchSpvsOnHold()
        break
      case 6:
        await entityStore.fetchFundsInFormation()
        break
      default:
        console.warn(`Unknown tab index: ${currentTabIndex.value}`)
    }
  } finally {
    skeleton.value = false
  }
}

watch(currentTabIndex, () => fetchTabData())

onMounted(async () => {
  await fetchTabData()
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6">
      <RouterLinkBack :to="`${rails_url()}/investors`" class="text-lg">
        <VButtonInvisible>← {{ capitalize(t('shared.back')) }}</VButtonInvisible>
      </RouterLinkBack>
    </div>
    <ChecklistEntitiesNav :tabs="tabs" />
    <AllGroup v-if="currentTabIndex == 0" :entities="allGroup" :skeleton="skeleton" />
    <ManageEntities
      v-else-if="[1, 2].includes(currentTabIndex)"
      :entities="manageEntities"
      :skeleton="skeleton"
    ></ManageEntities>
    <InProgress
      v-else-if="[3, 4, 5, 6].includes(currentTabIndex)"
      :entities="inProgress"
      :columns="entityStore.columnDefinitions"
      :skeleton="skeleton"
      :showOnHoldToggle="[4, 5].includes(currentTabIndex)"
      :emptyMessage="emptyMessageForTab"
    ></InProgress>
  </TheLayout>
</template>
